import React, { useState } from 'react';
import Button from '../ui/Button';
import SearchableSelect from '../ui/SearchableSelect';
import TagMultiSelect from '../ui/TagMultiSelect';
import { errorMessageOf } from '../../utils/apiErrors';
import type { Workspace } from '../../utils/workspaces';

interface AddToProjectPanelProps {
  /** Projects of this workspace the person is not on yet. */
  projects: readonly Workspace[];
  knownTeams: readonly string[];
  onAdd: (projectId: string, teams: string[]) => Promise<void>;
  onCancel: () => void;
}

/** Puts a person on a project's team, with the teams they belong to there. */
const AddToProjectPanel: React.FC<AddToProjectPanelProps> = ({ projects, knownTeams, onAdd, onCancel }) => {
  const [chosen, setChosen] = useState('');
  const [teams, setTeams] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const projectId = projects.some(p => p.project_id === chosen) ? chosen : projects[0]?.project_id ?? '';

  if (projects.length === 0) {
    return (
      <div className="people-panel">
        <p className="settings-muted-xs">This person is already on every project in the workspace.</p>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Close</Button>
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || !projectId) return;
    setSaving(true);
    setError(null);
    try {
      await onAdd(projectId, teams);
    } catch (err) {
      setError(errorMessageOf(err, "Couldn't add this person to the project. Please try again."));
      setSaving(false);
    }
  };

  return (
    <form className="people-panel" onSubmit={submit} noValidate>
      <div className="form-group">
        <span className="form-label">Project</span>
        <SearchableSelect
          value={projectId}
          onChange={setChosen}
          options={projects.map(p => ({ value: p.project_id, label: p.project_name }))}
        />
      </div>
      <div className="form-group">
        <span className="form-label">Teams (optional)</span>
        <TagMultiSelect values={teams} onChange={setTeams} options={[...knownTeams]} placeholder="Pick or add a team" />
      </div>
      {error && <p className="form-error-text" role="alert">{error}</p>}
      <div className="people-form__actions">
        <Button type="submit" variant="primary" size="sm" loading={saving}>Add</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
};

export default AddToProjectPanel;
